import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMap } from '@vis.gl/react-google-maps';
import {
    getKmlFeatureStyle, loadKmlGeoJson,
} from '../../helper-classes/kml-to-geojson';
import { createKmlInfoContent } from '../../helper-classes/kml-feature-details';

const KmlLayer = ({ url, zoomToLayer, zoomRequest }) => {
    const map = useMap();
    const [bounds, setBounds] = useState(null);

    useEffect(() => {
        if (!map || !window.google?.maps) return undefined;
        const controller = new AbortController();
        const layer = new window.google.maps.Data();
        const infoWindow = new window.google.maps.InfoWindow();
        const reportError = (loadError) => {
            if (controller.signal.aborted) return;
            layer.setMap(null);
            /* eslint-disable no-console */
            console.error('Error loading KML overlay:', url, loadError);
        };
        const dataListeners = [];
        setBounds(null);

        loadKmlGeoJson(url, controller.signal)
            .then(async (geoJson) => {
                if (controller.signal.aborted || !geoJson) return;
                layer.addGeoJson({ type: geoJson.type, features: geoJson.features });
                const featureStyles = new Map();
                const iconDimensions = new Map();
                const pendingStyles = [];
                const layerBounds = new window.google.maps.LatLngBounds();
                layer.forEach((feature) => {
                    pendingStyles.push(
                        getKmlFeatureStyle(feature, url, iconDimensions).then((style) => {
                            featureStyles.set(feature, style);
                        }),
                    );
                    const geometry = feature.getGeometry();
                    if (geometry) geometry.forEachLatLng(latLng => layerBounds.extend(latLng));
                });
                await Promise.all(pendingStyles);
                if (controller.signal.aborted) return;
                layer.setStyle(feature => featureStyles.get(feature));
                const showInfo = (details, balloon, position) => {
                    try {
                        const content = createKmlInfoContent(details, balloon);
                        if (!content) {
                            infoWindow.close();
                            return;
                        }
                        infoWindow.setContent(content);
                        infoWindow.setPosition(position);
                        infoWindow.open({ map });
                    } catch (detailsError) {
                        reportError(detailsError);
                    }
                };
                dataListeners.push(layer.addListener('click', (event) => {
                    showInfo(event.feature.getProperty('kmlDetails'),
                        event.feature.getProperty('kmlStyles')?.balloon, event.latLng);
                }));
                layer.setMap(map);
                setBounds(layerBounds);
            })
            .catch(reportError);

        return () => {
            controller.abort();
            dataListeners.forEach(listener => listener.remove());
            infoWindow.close();
            layer.setMap(null);
        };
    }, [map, url]);

    useEffect(() => {
        if (map && zoomToLayer && bounds && !bounds.isEmpty()) map.fitBounds(bounds);
    }, [map, zoomToLayer, zoomRequest, bounds]);

    return null;
};

KmlLayer.propTypes = {
    url: PropTypes.string.isRequired,
    zoomToLayer: PropTypes.bool.isRequired,
    zoomRequest: PropTypes.number.isRequired,
};

export default KmlLayer;
